import qs from "qs";
import { GetData, GetDataV2, GetDataWithErrors } from "./Functions/SettingData";

// Serialize array params as repeated keys (reason=A&reason=B), NOT bracket
// notation (reason[]=A) which the Flask reqparse `append` args can't read.
const repeatArrayParams = (params) => qs.stringify(params, { arrayFormat: "repeat" });

// API module for the Battery Parts Replacement & Tracking feature.
// Endpoints are documented in battery-parts-replacement-backend-contract.md.
// Functions are added per frontend slice as they are needed.

// §3.1 — Battery components tree (strings -> packs). Same envelope/node
// contract as /api/digital_twin/battery_config/v3/<batteryID>.
export const getBatteryComponents = async (batteryID) => {
    const config = {
        method: "get",
        url: `/api/batteries/${batteryID}/components`,
        headers: {
            "Content-Type": "application/json",
        },
    };
    return GetData(config);
};

// §3.2 — Service records (Replacement Logs) for a battery. Also backs the
// per-pack / battery-level Event History drawer (filtered client-side by pack).
// Search autocomplete for the Replacement Logs table. `searchBy` picks which
// identifier is suggested -- "serialNumber" (the default) or "requestID" -- and
// matches the search box's field dropdown. Returns the standard GetData envelope
// ({ responseStatus, response: { data: [...] } }) the shared TableSearch
// suggestions handler consumes.
export const getServiceRecordSearchSuggestions = async (
    batteryID,
    search,
    limit = 5,
    searchBy = "serialNumber"
) => {
    const config = {
        method: "get",
        url: `/api/batteries/${batteryID}/service-records/serial-suggestions`,
        params: { search, limit, searchBy },
        headers: {
            "Content-Type": "application/json",
        },
    };
    return GetData(config);
};

export const getBatteryServiceRecords = async (batteryID, params = {}) => {
    const config = {
        method: "get",
        url: `/api/batteries/${batteryID}/service-records`,
        params,
        paramsSerializer: repeatArrayParams,
        headers: {
            "Content-Type": "application/json",
        },
    };
    return GetData(config);
};

// § NEW — Operational cross-check. Returns per vehicle:
// { vehicleID, currentOperationalStatus, lastNonOperationalAt, hasReplacementSince }.
export const getReplacementStatus = async (vehicleIDs) => {
    const config = {
        method: "post",
        url: `/api/vehicles/replacement-status`,
        data: { vehicleIDs },
        headers: {
            "Content-Type": "application/json",
        },
    };
    return GetDataV2(config);
};

// §C9 — CSV / email export for the Replacement Logs table. Body carries the
// same filters plus actionType ("download-csv" | "mail"), columns/columnOrder,
// and (for mail) emailTo/emailCc/subject/body/fileName.
export const serviceRecordsListAction = async (batteryID, body) => {
    const config = {
        method: "post",
        url: `/api/batteries/${batteryID}/service-records/list-action`,
        data: body,
        headers: {
            "Content-Type": "application/json",
        },
    };
    return GetDataV2(config);
};

// §3.6 — Full service record (same shape as a 3.2 row + metricsSnapshot).
export const getServiceRecordDetail = async (serviceRecordID) => {
    const config = {
        method: "get",
        url: `/api/service-records/${serviceRecordID}`,
        headers: {
            "Content-Type": "application/json",
        },
    };
    return GetData(config);
};

// §3.7b — Relative path to the attached-documents zip for a record. The table's
// row download opens this (cookie-authenticated GET streams the zip).
export const getServiceRecordArchivePath = (serviceRecordID) =>
    `/api/service-records/${serviceRecordID}/documents/archive`;

// §3.7a — Relative path to a single attached document. redirect=true 302s to a
// short-lived presigned S3 URL; opened in a new tab (cookie-authenticated GET).
export const getServiceRecordDocumentDownloadPath = (serviceRecordID, documentID) =>
    `/api/service-records/${serviceRecordID}/documents/${documentID}?redirect=true`;

// §3.3 — Two-point Bus Information preview. Returns current + held-up metric
// sets, deltas, and a stateless `snapshotToken` that is passed verbatim to
// create (preview == saved). busHeldUpAt is an ISO-ish "YYYY-MM-DD HH:mm:ss".
export const getMetricsPreview = async (batteryID, busHeldUpAt) => {
    const config = {
        method: "get",
        url: `/api/service-records/metrics-preview`,
        params: { batteryID, busHeldUpAt },
        headers: {
            "Content-Type": "application/json",
        },
    };
    return GetData(config);
};

// §3.5a — Stage a single document (multipart). Returns
// { documentID, docType, fileName, sizeBytes, status:"PENDING" }.
export const uploadServiceDocument = async (file, docType, batteryID, onUploadProgress) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("docType", docType);
    formData.append("batteryID", batteryID);

    const config = {
        method: "post",
        url: `/api/service-records/documents`,
        data: formData,
        headers: {
            "Content-Type": "multipart/form-data",
        },
        ...(onUploadProgress ? { onUploadProgress } : {}),
    };
    return GetDataV2(config);
};

// §3.5a — Remove a still-PENDING document owned by the caller.
export const deleteServiceDocument = async (documentID) => {
    const config = {
        method: "delete",
        url: `/api/service-records/documents/${documentID}`,
        headers: {
            "Content-Type": "application/json",
        },
    };
    return GetDataV2(config);
};

// §3.5b — Create the service record (atomic). Body carries snapshotToken,
// documentIDs, remarks/acknowledgment, serviceDetails and packReplacementDetails.
export const createServiceRecord = async (payload) => {
    const config = {
        method: "post",
        url: `/api/service-records`,
        data: payload,
        headers: {
            "Content-Type": "application/json",
        },
    };
    return GetDataV2(config);
};

// Edit Pack Details — correct one or more packs in ONE atomic request.
//
// Always a list, even for a single pack: the backend has one endpoint and one
// validation path, so a flat single-pack body would be a second shape to keep in step.
// Each entry is keyed on absPackNo, NOT packConfigID — a count-only slot has no
// packConfig row (and so no packConfigID) yet it is exactly the slot most likely to
// need its serial filled in.
//
// Each entry's `expected` must carry all three fields as that card rendered them. The
// backend 409s if any has moved, including one this user never touched.
//
// All-or-nothing server side: a conflict on any pack rolls the rest back, so the user
// is never left guessing which half of their correction applied, and each per-pack
// error names its slot.
//
// GetDataWithErrors, not GetDataV2: GetDataV2 is a bare axios call, so every non-2xx
// REJECTS and the caller only ever sees "Request failed with status code 409". This
// endpoint's whole value is in its messages, so the envelope has to survive a 4xx.
export const updatePackDetails = async (batteryID, payload) => {
    const config = {
        method: "put",
        url: `/api/batteries/${batteryID}/packs`,
        data: payload,
        headers: {
            "Content-Type": "application/json",
        },
    };
    return GetDataWithErrors(config);
};
